/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useContext, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';
import ButtonSave from '@/components/button-save';
import ClickAway from '@/components/click-away/click-away';
import PermissionGate from '@/components/permission-gate';
import { getStaticCdnPath } from '@/utilities/static-build';

/**
 * Component that has an editable text box, and an edit button. The box is only editable once
 * you click the edit button, upon which a save button appears. Click save to commit the changes.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {func} onChange - Called when the save button is clicked
 * @param {string} content - The text content of the input box
 */
function EditableText({
  inputSelectorId,
  onChange,
  content,
  maxLength,
  textDisabled,
  ariaLabel,
  editPermissionId,
}) {
  const { textDirection } = useContext(LocaleContext);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const initialValue = useRef(content);

  useEffect(() => {
    if (editing) {
      inputRef?.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    initialValue.current = content;
  }, [content]);

  const keyDownToggleEditing = (event) => {
    if (event.key === 'Enter') {
      setEditing(true);
    }
  };

  const textSaved = () => {
    const newText = inputRef?.current?.value;
    if (newText.trim().length === 0) {
      inputRef.current.value = content;
    } else {
      onChange(newText);
    }
    setEditing(false);
  };

  const resetInput = () => {
    inputRef.current.value = initialValue.current;
  };

  const handleClickAway = () => {
    setEditing(false);
    resetInput();
  };

  const keyDownSave = (event) => {
    if (event.key === 'Enter') {
      textSaved();
    }
  };

  const handleStartEditing = () => {
    initialValue.current = inputRef.current?.value;
  };

  return (
    <>
      <ClickAway shouldWeListen={editing} onClickAway={handleClickAway}>
        <div className="wrapper">
          <input
            aria-label={ariaLabel}
            id={inputSelectorId}
            ref={inputRef}
            type="text"
            dir={textDirection}
            defaultValue={content}
            disabled={!editing}
            maxLength={maxLength}
            className={`input${textDisabled ? '-disabled' : ''}`}
            onFocus={handleStartEditing}
          />
          {editing ? (
            <div className="save">
              <ButtonSave
                inputSelectorId={`${inputSelectorId}-save`}
                onClick={textSaved}
                onKeyDown={(e) => keyDownSave(e)}
              />
            </div>
          ) : (
            <PermissionGate permission={editPermissionId}>
              <div
                className="edit"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(true);
                }}
                onKeyDown={(e) => keyDownToggleEditing(e)}
                onFocus={handleClickAway}
                aria-label="Edit"
              >
                <img src={`${getStaticCdnPath()}/icons/edit.svg`} alt="" className="icon-image" />
              </div>
            </PermissionGate>
          )}
        </div>
      </ClickAway>
      <style jsx>
        {`
          .wrapper {
            position: relative;
          }

          input {
            border: none;
            color: var(--color-primary-dark);
            font-family: inherit;
            font-size: var(--font-size-body);
            width: 100%;
            height: 64px;
            background-color: var(--color-white);
          }

          .input-disabled {
            color: var(--color-mid-grey);
          }

          .edit {
            position: absolute;
            right: 0;
            border-left: 1px solid var(--color-line-grey);
            padding: 0 12px;
            cursor: pointer;
            bottom: 20px;
          }

          .save {
            position: absolute;
            right: 0;
            padding: 0 20px;
            cursor: pointer;
            bottom: 16px;
          }
        `}
      </style>
    </>
  );
}

EditableText.defaultProps = {
  inputSelectorId: null,
  onChange: () => {},
  maxLength: null,
  textDisabled: false,
  ariaLabel: null,
};

EditableText.propTypes = {
  inputSelectorId: PropTypes.string,
  onChange: PropTypes.func,
  content: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  textDisabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default EditableText;
